<script setup lang="ts">
import { ref, onMounted } from 'vue';
import moment from 'moment';
import NumerationPolicyRepository from '../../repositories/numerationPolicies.repository';
import CompaniesRepository from '@repositories/companies.repository';
import AssetsRepository from '@repositories/assets.repository';
import { ICompany } from '../../types/company.type';
import { useI18n } from 'vue-i18n';
import { IAsset } from 'src/types/assets.type';
import DataTable from '@components/DataTable.vue';
import { useToast } from '../../composables/useToast';

const isLoadingNumerationPolicies = ref<boolean>(false);

const { t } = useI18n();
const { $toast } = useToast();
const numerationPolicyRepository = new NumerationPolicyRepository();
const numerationPoliciesData = ref([]);
const paginationData = ref();
const currentPage = ref<number>();
const refTableForm = ref<any>(null);
const companies = ref<ICompany[]>([]);
const assets = ref<IAsset[]>([]);

const getNumeration = async (page: number) => {
  isLoadingNumerationPolicies.value = true;
  try {
    const { data } = await numerationPolicyRepository.getNumerationPolicies({
      page,
    });
    numerationPoliciesData.value = data.numerationPolicies ?? [];
    paginationData.value = data.pagination;
  } finally {
    isLoadingNumerationPolicies.value = false;
  }
};

const getCompaniesFromApi = async () => {
  const data = await CompaniesRepository.getCompanies();

  companies.value = data.companies
    .filter((company: ICompany) => company.requestType === 'API')
    .map((company: ICompany) => ({
      id: company.id,
      nombre: company.name,
    }));

  const apiKeyIdField = fields.value.find(
    (field: any) => field.key === 'companyId',
  );
  if (apiKeyIdField) {
    apiKeyIdField.options = companies.value;
  }
};

const getAsset = async () => {
  const assets = await AssetsRepository.getAssetsAll();
  assets.value = assets.data.map((asset: IAsset) => ({
    id: asset.id,
    nombre: asset.description,
  }));

  const apiKeyIdField = fields.value.find(
    (field: any) => field.key === 'targetAssetId',
  );
  if (apiKeyIdField) {
    apiKeyIdField.options = assets.value;
  }
};

onMounted(async () => {
  getNumeration(1);
  getCompaniesFromApi();
  getAsset();
});

const fields: any = ref([
  {
    key: 'assetID',
    title: t('numeros_polizas.nueva_numeracion'),
    type: 'text',
    placeholder: t('numeros_polizas.nueva_numeracion'),
    value: null,
    cols: 3,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
  },
  {
    key: 'email1',
    title: t('numeros_polizas.email1'),
    type: 'email',
    placeholder: t('numeros_polizas.email1'),
    value: null,
    cols: 3,
  },
  {
    key: 'email2',
    title: t('numeros_polizas.email2'),
    type: 'email',
    placeholder: t('numeros_polizas.email2'),
    value: null,
    cols: 3,
  },
  {
    key: 'email3',
    title: t('numeros_polizas.email3'),
    type: 'email',
    placeholder: t('numeros_polizas.email3'),
    value: null,
    cols: 3,
  },
  {
    key: 'companyId',
    title: t('global.sponsor'),
    type: 'select',
    placeholder: t('global.sponsor'),
    value: null,
    selectLabel: null,
    cols: 3,
    options: [],
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
  },
  {
    key: 'typePolicyTransportId',
    title: t('numeros_polizas.tipo_de_poliza'),
    type: 'select',
    placeholder: t('numeros_polizas.tipo_de_poliza'),
    value: null,
    selectLabel: null,
    cols: 3,
    options: [
      { nombre: 'Terrestre', id: 1 },
      { nombre: 'Maritima', id: 2 },
      { nombre: 'Paquetería', id: 3 },
      { nombre: 'Container', id: 6 },
    ],
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
  },
  {
    key: 'targetAssetId',
    title: t('numeros_polizas.tipo_de_mercaderia'),
    type: 'select',
    placeholder: t('numeros_polizas.tipo_de_mercaderia'),
    value: null,
    selectLabel: null,
    cols: 3,
    options: [],
  },
]);

const fieldsTable: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'assetID',
    label: t('numeros_polizas.nueva_numeracion'),
  },
  {
    key: 'createdAt',
    label: t('numeros_polizas.fecha_de_inicio'),
    formatter: (e: any) => moment(e).format('DD-MM-YYYY HH:mm'),
  },
  {
    key: 'endAt',
    label: t('numeros_polizas.fecha_de_fin'),
    formatter: (e: any) =>
      !e
        ? t('numeros_polizas.sin_fecha_de_fin')
        : moment(e).format('DD-MM-YYYY HH:mm'),
  },
  {
    key: 'email1',
    label: t('numeros_polizas.email1'),
  },
  {
    key: 'email2',
    label: t('numeros_polizas.email2'),
  },
  {
    key: 'email3',
    label: t('numeros_polizas.email3'),
  },
  {
    key: 'apiKey',
    label: t('global.sponsor'),
    formatter: (e: any) => e?.appName,
  },
  {
    key: 'typePolicyTransport',
    label: t('numeros_polizas.tipo_de_poliza'),
    formatter: (e: any) => e?.typeName,
  },
  {
    key: 'targetAsset',
    label: t('numeros_polizas.tipo_de_mercaderia'),
    formatter: (e: any) => e?.description,
  },
]);

const handleOnSubmit = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;

  const newNumerationPolicy = {
    ...items,
    companyId: items.companyId.id,
    typePolicyTransportId: items.typePolicyTransportId.id,
    targetAssetId: items.targetAssetId?.id ?? null,
  };

  try {
    await numerationPolicyRepository.createNumerationPolicies(
      newNumerationPolicy,
    );
  } catch (error: any) {
    $toast?.error(error?.message ?? t('global.ocurrio_un_error'));
    return;
  }

  getNumeration(1);

  if (!refTableForm.value) return;
  await refTableForm.value.resetValues();
};

const onChangePage = async (page: any) => {
  getNumeration(page);
  currentPage.value = page;
};
</script>

<template>
  <Layout
    :title="t('numeros_polizas.actualizar_poliza_chubb')"
    :textColor="'color-secondary'"
  >
    <template #layout>
      <Form
        ref="refTableForm"
        :inputs="fields"
        @onSubmit="handleOnSubmit"
        
      >
        <template #buttons>
          <CButton
            class="background-blue button__form"
            style="padding: 0.55rem !important"
          >
            <template #content>{{
              t('numeros_polizas.actualizar_numeracion')
            }}</template>
          </CButton>
        </template>
      </Form>
      <DataTable
        :loading="isLoadingNumerationPolicies"
        :fields="fieldsTable"
        :items="numerationPoliciesData"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
      </DataTable>
    </template>
  </Layout>
</template>
